import type { AxiosRequestConfig } from "axios";
import { z } from "zod";

import { AilmentSchema } from "api/schemas/shared/Treatment";
import { isStage } from "utils/misc/getBuildEnvironment";

export const CeStructureSchema = z.enum(["digital", "hybrid", "invited_non_rtm", "none", "non_rtm", "rtm"]);
export type CeStructure = z.infer<typeof CeStructureSchema>;

export type GetPatientsArgsSchema = {
  userID: number;
  config?: AxiosRequestConfig<unknown> | undefined;
};

const PaymentMethodSchema = z.enum(["exemption_card", "free_care", "invoice", "none"]).nullable();
export type PaymentMethod = z.infer<typeof PaymentMethodSchema>;

const PrimaryJointSchema = z.object({
  location: z.string().nullable(),
  lateral_location: z.string().nullable(),
});
export type PrimaryJoint = z.infer<typeof PrimaryJointSchema>;

const JointOrAilmentSchema = z.object({
  ailment: AilmentSchema,
  joint: PrimaryJointSchema,
});

export type JointOrAilment = z.infer<typeof JointOrAilmentSchema>;

export const PatientsSchema = z.object({
  adherence: z.number().optional(),
  address_state: z
    .object({
      code: z.string(),
      name: z.string(),
    })
    .nullable()
    .optional(),
  ailment: AilmentSchema,
  appointment_to_schedule: z.string().nullable(),
  awaiting_treatment_ready: z.boolean(),
  billable_care_events: z.number().optional(),
  ce_structure: CeStructureSchema,
  deleted: z.boolean(),
  email: z.string(),
  // Workaround for test patients not having a first_name
  first_name: isStage() ? z.string().nullable() : z.string(),
  id: z.number(),
  last_activity_completed_at: z.string().nullable().optional(),
  last_medical_referral_signature_at: z.string().nullable().optional(),
  // Workaround for test patients not having a last_name
  last_name: isStage() ? z.string().nullable() : z.string(),
  new_comments: z.boolean(),
  new_messages: z.boolean(),
  new_patient: z.boolean(),
  npo_consent: z.boolean().nullable(),
  payer_name: z.string(),
  payment_method: PaymentMethodSchema,
  // Workaround for test patients not having a preferred_name
  preferred_name: isStage() ? z.string().nullable() : z.string(),
  premium_activation_occurred_at: z.string().optional().nullable(),
  premium_activation_state: z.string().optional(),
  premium_type: z.enum(["none", "selfcare", "guided_care"]),
  primary_joint: PrimaryJointSchema.optional().nullable(),
  prio: z.boolean(),
  referral_process_status: z
    .enum(["questionnaire_submitted", "waiting_for_referral_upload", "rejected", "referral_uploaded"])
    .nullable(),
  state: z.enum([
    "discharged",
    "explore",
    "in_custom_course",
    "in_introduction",
    "in_onboarding",
    "in_selfcare",
    "in_six_week_course",
    "in_sustain_course",
    "invited",
    "new",
    "ready",
    "treatment_ended",
  ]),
  status: z.string(),
  subscription: z
    .object({
      status: z.string().nullable(),
      is_free_trial: z.boolean().optional(),
    })
    .optional(),
  therapist_assignment_role: z.enum(["main_therapist", "substitute_therapist"]),
  us_insurance_profiles: z
    .array(
      z.object({
        claim_order_type: z.string(),
        id: z.number().nullable(),
        immediate_referral_required: z.boolean().nullable(),
        insurance_company_name: z.string(),
        insurance_company_supported: z.boolean().nullable(),
        medicare: z.boolean().optional(),
        pre_authorization_required: z.boolean().nullable(),
        status: z.string(),
      })
    )
    .optional(),
  us_provider_group_profile: z
    .object({
      name: z.string().optional(),
    })
    .optional(),
  week: z.number(),
});

export type Patients = z.infer<typeof PatientsSchema>;
